import React, { useState, useEffect, useRef } from "react";
import { GridColDef } from "@mui/x-data-grid";
import { Visibility } from "@mui/icons-material";
import {
  Box, TableHead, TableRow, Grid, TableBody, TableCell,
  Button, Table, Typography, TableContainer, Stack,
  IconButton, Select, MenuItem, FormControl,
  alpha, useTheme, useMediaQuery, Paper, Chip, CircularProgress,
} from "@mui/material";
import BarChartIcon from "@mui/icons-material/BarChart";
import FilterListIcon from "@mui/icons-material/FilterList";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import DataGrid from "components/table/DataGrid";
import dayjs from "dayjs";
import { httpClient } from "Service/Service";

interface Service {
  name: string;
  endpoint: string;
  columns: GridColDef[];
  serviceName?: string;
}

interface HistoryReportProps {
  title: string;
  services: Service[];
}

const DateWiseReport: React.FC<HistoryReportProps> = ({ title, services }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [summaryData, setSummaryData] = useState<any[]>([]);
  const [detailData, setDetailData] = useState<any[]>([]);
  const [firstDate, setFirstDate] = useState<any>(dayjs().startOf("month"));
  const [lastDate, setLastDate] = useState<any>(dayjs());
  const [loading, setLoading] = useState(false);
  const [selectedService, setSelectedService] = useState<Service>(services[0]);
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      fetchData();
      isFirstRender.current = false;
    }
  }, []);

  const fetchData = async () => {
    if (!selectedService) return;
    try {
      setLoading(true);
      setDetailData([]);
      setSummaryData([]);
      const result = await httpClient(selectedService.endpoint, "POST", {
        start_date: firstDate,
        end_date: lastDate,
        serviceName: selectedService.serviceName,
      });
      setSummaryData(Array.isArray(result?.data) ? result.data : []);
    } catch {
      setSummaryData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (index: number) => {
    if (summaryData[index]?.customers) setDetailData(summaryData[index].customers);
  };

  const handleServiceChange = (event: any) => {
    const service = services.find((s) => s.name === event.target.value);
    if (service) {
      setSelectedService(service);
      setSummaryData([]);
      setDetailData([]);
    }
  };

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh" }}>
      {/* Page Header */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.secondary.dark} 0%, ${theme.palette.secondary.main} 60%, ${theme.palette.secondary.light} 100%)`,
          px: { xs: 2, sm: 4 },
          py: { xs: 2.5, sm: 3.5 },
          display: "flex",
          alignItems: "center",
          borderRadius: 1,
          gap: 2,
          boxShadow: `0 4px 20px ${alpha(theme.palette.secondary.main, 0.4)}`,
        }}
      >
        <Box sx={{ bgcolor: alpha("#fff", 0.15), borderRadius: 2, p: 1, display: "flex", alignItems: "center" }}>
          <BarChartIcon sx={{ color: "#fff", fontSize: { xs: 28, sm: 36 } }} />
        </Box>
        <Box>
          <Typography variant={isMobile ? "h5" : "h4"} fontWeight={700} sx={{ color: "#fff", lineHeight: 1.2 }}>
            {title}
          </Typography>
          <Typography variant="body2" sx={{ color: alpha("#fff", 0.8), mt: 0.3 }}>
            View date-wise summary and detailed records
          </Typography>
        </Box>
      </Box>

      <Box sx={{ px: { xs: 1, sm: 3 }, pt: 2, pb: 4 }}>
        {/* Filter Card */}
        <Box
          sx={{
            bgcolor: "background.paper",
            borderRadius: 3,
            boxShadow: `0 2px 12px ${alpha(theme.palette.grey[500], 0.12)}`,
            overflow: "hidden",
            mb: 2,
          }}
        >
          <Box
            sx={{
              px: { xs: 1.5, sm: 2 },
              py: 1.25,
              bgcolor: alpha(theme.palette.secondary.main, 0.04),
              borderBottom: `1px solid ${theme.palette.divider}`,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <FilterListIcon sx={{ fontSize: 16, color: "secondary.main" }} />
            <Typography variant="subtitle2" fontWeight={700} color="secondary.main">
              Filter Options
            </Typography>
          </Box>

          <Box sx={{ px: { xs: 1.5, sm: 3 }, py: 2.5 }}>
            <Grid container spacing={2} alignItems="flex-end">
              {/* Service selector */}
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={0.5}>
                  Report Type
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    value={selectedService.name}
                    onChange={handleServiceChange}
                  >
                    {services.map((s) => (
                      <MenuItem key={s.name} value={s.name}>
                        {s.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Date pickers */}
              <Grid item xs={12} sm={12} md={6}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={0.5}>
                  Date Range
                </Typography>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DemoContainer components={["DatePicker", "DatePicker"]} sx={{ pt: 0 }}>
                    <DatePicker
                      label="Start date"
                      value={firstDate}
                      onChange={(d) => setFirstDate(d)}
                      format="DD-MM-YYYY"
                      slotProps={{
                        textField: { size: "small" },
                        openPickerIcon: { sx: { color: "secondary.main" } },
                      }}
                    />
                    <DatePicker
                      label="End date"
                      value={lastDate}
                      onChange={(d) => setLastDate(d)}
                      format="DD-MM-YYYY"
                      slotProps={{
                        textField: { size: "small" },
                        openPickerIcon: { sx: { color: "secondary.main" } },
                      }}
                    />
                  </DemoContainer>
                </LocalizationProvider>
              </Grid>

              {/* Fetch button */}
              <Grid item xs={12} sm={6} md={3}>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={fetchData}
                  fullWidth
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <BarChartIcon />}
                  sx={{
                    borderRadius: 2,
                    fontWeight: 600,
                    textTransform: "none",
                    height: 40,
                    boxShadow: `0 4px 12px ${alpha(theme.palette.secondary.main, 0.35)}`,
                  }}
                >
                  {loading ? "Loading..." : "Fetch Report"}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Box>

        {/* Summary Table */}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 8 }}>
            <CircularProgress color="secondary" />
          </Box>
        ) : summaryData.length > 0 ? (
          <Box
            sx={{
              bgcolor: "background.paper",
              borderRadius: 3,
              boxShadow: `0 2px 12px ${alpha(theme.palette.grey[500], 0.12)}`,
              overflow: "hidden",
              mb: 2,
            }}
          >
            <Box
              sx={{
                px: { xs: 1.5, sm: 2 },
                py: 1.25,
                bgcolor: alpha(theme.palette.secondary.main, 0.04),
                borderBottom: `1px solid ${theme.palette.divider}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography variant="subtitle2" fontWeight={700} color="info.main">
                {selectedService.name} — Summary
              </Typography>
              <Chip
                label={`${summaryData.length} records`}
                size="small"
                sx={{
                  bgcolor: alpha(theme.palette.secondary.main, 0.1),
                  color: "primary.dark",
                  fontWeight: 600,
                  fontSize: "0.65rem",
                }}
              />
            </Box>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: alpha(theme.palette.secondary.main, 0.05) }}>
                    <TableCell sx={{ fontWeight: 700, width: 60 }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Count</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Details</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {summaryData.map((row, i) => (
                    <TableRow
                      key={i}
                      hover
                      sx={{
                        "&:nth-of-type(odd)": { bgcolor: alpha(theme.palette.grey[500], 0.02) },
                        "&:last-child td": { border: 0 },
                      }}
                    >
                      <TableCell>
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                          {i + 1}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {row.join_date || row.date}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={row.customers?.length ?? row.count ?? 0}
                          size="small"
                          sx={{
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            color: "primary.dark",
                            fontWeight: 700,
                            fontSize: "0.7rem",
                            minWidth: 36,
                          }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          color="secondary"
                          onClick={() => handleViewDetails(i)}
                          sx={{
                            bgcolor: alpha(theme.palette.secondary.main, 0.08),
                            "&:hover": { bgcolor: alpha(theme.palette.secondary.main, 0.16) },
                          }}
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        ) : (
          <Paper
            elevation={0}
            sx={{
              p: 5,
              textAlign: "center",
              border: `1px dashed ${theme.palette.divider}`,
              borderRadius: 3,
            }}
          >
            <BarChartIcon sx={{ fontSize: 40, color: alpha(theme.palette.text.secondary, 0.3), mb: 1 }} />
            <Typography color="text.secondary">
              No data for the selected period. Adjust the filters and fetch again.
            </Typography>
          </Paper>
        )}

        {/* Detail Grid */}
        {detailData.length > 0 && (
          <Box
            sx={{
              borderRadius: 2,
              overflow: "hidden",
              border: `1px solid ${theme.palette.divider}`,
              "& .MuiPaper-root": { boxShadow: "none !important" },
            }}
          >
            <DataGrid
              title={`${selectedService.name} — Detail View`}
              columns={selectedService.columns}
              rows={detailData}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default DateWiseReport;
